import { MapPin, CalendarDays, Clock, ArrowUpRight, Users } from "lucide-react";
import { safeUrl } from "../../lib/safeUrl";
import { fundraiserImage, eventImage } from "../../lib/volunteerVisuals";
import { ShareMenu } from "../ShareMenu";
import { FavouriteButton } from "../FavouriteButton";
import { ParticipationButtons } from "../ParticipationButtons";
import { OrgLine, ViaNote } from "../OrgBadge";

const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
const daysLeft = (o) => {
  const end = o.event_end_date || o.event_date || o.expires_on;
  if (!end) return null;
  return Math.max(0, Math.ceil((new Date(end + "T23:59:59") - Date.now()) / 86400000));
};
const Btn = ({ href, children, primary, testId }) => (
  <a href={safeUrl(href)} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className={`inline-flex flex-1 items-center justify-center gap-1 rounded-full px-3 py-2.5 text-sm font-semibold ${primary ? "btn-green" : "border border-slate-300 text-slate-800 hover:border-[#1E3A8A]"}`}>
    {children}{primary && <ArrowUpRight size={15} />}
  </a>
);

export const ListingCard = ({ o, mode }) => {
  const cat = mode === "fundraiser" ? o.fundraiser_category : o.event_category;
  const left = daysLeft(o);
  return (
    <article className="card-rise flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white hover:shadow-lg" data-testid={`${mode}-card-${o.id}`}>
      <div className="relative h-36 overflow-hidden">
        <img src={mode === "fundraiser" ? fundraiserImage(cat, o.id) : eventImage(cat)} alt="" loading="lazy" className="h-full w-full object-cover" />
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-[#1E3A8A]" data-testid={`${mode}-card-category`}>{cat}</span>
        <FavouriteButton o={o} className="absolute right-3 top-3" />
      </div>
      <div className="flex flex-1 flex-col p-5">
        {mode === "event" && o.event_date && (
          <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid="event-card-date"><CalendarDays size={15} />{fmt(o.event_date)}{o.event_end_date && o.event_end_date !== o.event_date ? ` – ${fmt(o.event_end_date)}` : ""}</p>
        )}
        {mode === "fundraiser" && left != null && <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700" data-testid="fundraiser-days-left"><Clock size={15} />{left === 0 ? "Ends today" : `${left} day${left === 1 ? "" : "s"} remaining`}</p>}
        <h3 className="mt-2 text-lg font-semibold leading-snug text-slate-900" data-testid={`${mode}-card-title`}>{o.title}</h3>
        <div className="mt-2"><OrgLine o={o} label={mode === "fundraiser" ? "Hosted By" : "Organizer"} testId={`${mode}-org-${o.id}`} /></div>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-700" data-testid={`${mode}-card-impact`}>{o.description}</p>
        <div className="mt-3 space-y-1 text-sm text-slate-600">
          <p className="flex items-center gap-2"><MapPin size={15} className="text-slate-400" />{o.city}, {o.province}{o.distance_km != null && <span className="ml-auto font-semibold text-slate-800" data-testid={`${mode}-card-distance`}>{Math.round(o.distance_km)} km</span>}</p>
          {mode === "event" && <p className="flex items-center gap-2 text-emerald-700" data-testid="event-registration-status"><Users size={15} />Registration open · volunteers needed</p>}
          {mode === "fundraiser" && o.event_date && <p className="flex items-center gap-2" data-testid="fundraiser-upcoming-date"><CalendarDays size={15} className="text-slate-400" />Next date: {fmt(o.event_date)}</p>}
        </div>
        <div className="mt-auto pt-5"><ParticipationButtons o={o} context={mode} showSupporters={mode === "fundraiser"} /></div>
        <div className="flex flex-wrap gap-2 pt-2">
          {mode === "fundraiser" ? (
            <><Btn href={o.donate_url || o.url} primary testId={`fundraiser-donate-${o.id}`}>Donate</Btn><Btn href={o.apply_url || o.signup_url} testId={`fundraiser-volunteer-${o.id}`}>Volunteer</Btn></>
          ) : (
            <><Btn href={o.register_url || o.signup_url} primary testId={`event-attend-${o.id}`}>Register</Btn><Btn href={o.apply_url || o.signup_url} testId={`event-volunteer-${o.id}`}>Volunteer</Btn><Btn href={o.visit_url || o.url} testId={`event-sponsor-${o.id}`}>Sponsor</Btn></>
          )}
          <ShareMenu path={`/volunteer/${o.id}`} title={o.title} testId={`${mode}-share-${o.id}`} />
        </div>
        <ViaNote o={o} />
      </div>
    </article>
  );
};
